import type { Schemas } from '@argzombie/api-client';
import {
  arcDefaults,
  castDefaults,
  coverDefaults,
  endingDefaults,
  issueCastDefaults,
  issueStoryDefaults,
  locationRuleDefaults,
  storyIssueRulesDefaults,
  storyLoadoutDefaults,
  storyRewardsDefaults,
} from '../s4/defaults';
import {
  STORY_ARC_KINDS,
  STORY_LOCATION_KINDS,
  STORY_ON_FAIL,
  type ArcDef,
  type IssueStoryDef,
  type LocationRuleDef,
  type StoryLoadoutDef,
} from '../s4/types';
import { factSpec } from '../story/facts';
import { COND_OPS, type CondDef, type PanelSpec } from '../story/types';
import { withDefaults } from '../teamsDefaults';
import type { Err } from './common';
import { panelCell, type PanelCellContext } from './panels';
import { validateMissionRewards } from './triggers';
import { MISSION_VISIBILITIES, TRIGGER_STATUSES, type Bundle, type MissionDef } from './types';

/*
 * Port of shared-cs `ContentValidator.Story.cs` (`StorySections`, S4-SRV): storyRules, scenario lose rules' onFail, arcs, issues
 * (missions with a story block), their location rules, covers, intro/outro, dog-ears and rewards, and the arc.* flags each issue reads.
 * Same errors, same wording, same order. Absent blocks and fields are the C# initializers (the server's parser fills them in); only an
 * explicit null `arcs` or `storyRules` is "missing". The C# messages carry no location, so each one is placed on the section, entry
 * and dotted path a form shows it next to: `arcs` entries (`cast.2.name`), `missions` entries (`story.cover.title`), `storyRules`, and
 * `scenarios` (`lose.0.onFail`).
 */

export const MIN_ISSUES_PER_ARC = 3;
export const MAX_ISSUES_PER_ARC = 5;
export const MIN_CHECKPOINTS = 2;
export const MAX_CHECKPOINTS = 4;
/** Day Zero (ADR 0074, C3): a tutorial arc's issue may hold up to 10 dog-ears. */
export const MAX_TUTORIAL_CHECKPOINTS = 10;
export const MAX_ISSUE_STAGES = 2;
export const MAX_STORY_REWARD_STACKS = 8;
export const MAX_CAST_HEALTH = 500;
/** `ContentValidator.MaxGrantQuantity`. */
const MAX_GRANT_QUANTITY = 999;
/** `WorldFlags.ArcPrefix`. */
const ARC_PREFIX = 'arc.';

/** shared-cs `LocationRules.ExcludedKinds`: OSM kinds a story may never send anyone to (homes and places of worship). */
export const EXCLUDED_KINDS = [
  'place_of_worship',
  'church',
  'chapel',
  'cathedral',
  'mosque',
  'temple',
  'synagogue',
  'shrine',
  'monastery',
  'house',
  'residential',
  'apartments',
  'detached',
  'semidetached_house',
  'terrace',
  'bungalow',
  'dormitory',
  'farm',
  'farmhouse',
  'home',
  'residence',
] as const;
/** shared-cs `LocationRules.ExcludedCategories`. */
export const EXCLUDED_CATEGORIES = ['residential', 'home', 'house'] as const;

type ItemDef = Schemas['ItemDef'];
type ScenarioDef = Schemas['ScenarioDef'];
type MissionStageDef = Schemas['MissionStageDef'];
type At = (path: string, message: string) => void;

const s = (v: unknown) => (v == null ? '' : String(v));
const isBlank = (v: string | null | undefined) => !v || v.trim() === '';
const has = (list: readonly string[], v: string | null | undefined) =>
  v != null && list.includes(v);

/** `BuildingKinds.Normalize`. */
export function normalizeKind(value: string | null | undefined): string {
  if (!value) return '';
  let v = value.trim().toLowerCase().replace(/ /g, '_').replace(/-/g, '_');
  const semi = v.indexOf(';');
  if (semi >= 0) v = v.slice(0, semi);
  return v.length > 32 ? v.slice(0, 32) : v;
}

/** `LocationRules.IsExcludedKind`. */
const isExcludedKind = (k: string | null | undefined) =>
  !!k && (EXCLUDED_KINDS as readonly string[]).includes(k.toLowerCase());

/** `LocationRules.ExcludedTargets`: the kinds and categories of a rule that could mean a home or a place of worship. */
export function excludedTargets(rule: LocationRuleDef | null | undefined): string[] {
  const bad: string[] = [];
  for (const k of rule?.kinds ?? []) if (isExcludedKind(k)) bad.push(k);
  for (const c of rule?.categories ?? [])
    if ((EXCLUDED_CATEGORIES as readonly string[]).includes(c ?? '')) bad.push(c);
  return bad;
}

/** `WorldFlags.Matches`: a pattern with `{name}` segments against a concrete key. */
export function flagMatches(pattern: string | null | undefined, key: string | null | undefined) {
  if (pattern == null || key == null) return false;
  if (pattern === key) return true;
  const p = pattern.split('.');
  const k = key.split('.');
  if (p.length !== k.length) return false;
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const placeholder = seg.length > 2 && seg[0] === '{' && seg[seg.length - 1] === '}';
    if (placeholder ? k[i]!.length === 0 : seg !== k[i]) return false;
  }
  return true;
}

/** `WorldFlags.Find`: an exact key first, then the first matching pattern. */
function findFlag(b: Bundle, key: string) {
  const list = (b.worldFlags ?? []).filter((d) => d != null);
  if (!key) return undefined;
  return list.find((d) => d.key === key) ?? list.find((d) => flagMatches(d.key, key));
}

/** `ContentValidator.SignsKey`: `bld.{id}.x` with the id filled in, or `bld.<id>.<name>`. */
export const signsKey = (signsFlag: string, buildingId: number) =>
  signsFlag.includes('{id}')
    ? signsFlag.replace(/\{id\}/g, String(buildingId))
    : `bld.${buildingId}.${signsFlag}`;

const isCombinator = (c: CondDef) => !!c.all?.length || !!c.any?.length || c.not != null;

/** `StoryConditions.Leaves`: every leaf under a condition, in order (all, any, not). */
export function condLeaves(c: CondDef | null | undefined): CondDef[] {
  if (c == null) return [];
  if (!isCombinator(c)) return c.fact ? [c] : [];
  return [
    ...(c.all ?? []).flatMap((x) => condLeaves(x)),
    ...(c.any ?? []).flatMap((x) => condLeaves(x)),
    ...condLeaves(c.not),
  ];
}

/** `ContentValidator.ArcFlags`: the `arc.*` flags a condition reads. */
function arcFlagsOf(c: CondDef | null | undefined): string[] {
  return condLeaves(c)
    .map((l) => l.fact ?? '')
    .filter((f) => f.startsWith(`flag.${ARC_PREFIX}`))
    .map((f) => f.slice(5));
}

/** `ContentValidator.StoryCondition`: a story condition reads only server facts. */
function storyCondition(c: CondDef | null | undefined, where: string, at: (m: string) => void) {
  for (const leaf of condLeaves(c)) {
    const spec = factSpec(leaf.fact);
    if (spec == null || spec.where !== 'server')
      at(
        `${where} reads '${s(leaf.fact)}', which a story condition can't (use flag.* or ledger.*).`,
      );
    else if (!has(COND_OPS, leaf.op))
      at(`${where} compares '${s(leaf.fact)}' with unknown op '${s(leaf.op)}'.`);
  }
}

/** A list as the C# sees it: absent = the initializer (empty); entries kept with their index (nulls included). */
const listOf = <T>(v: (T | null)[] | null | undefined): (T | null)[] => v ?? [];

/** `ContentValidator.WrittenBy`: a read flag is written when a written key equals it or is a pattern that matches it. */
const writtenBy = (written: Set<string>, flag: string) =>
  written.has(flag) || [...written].some((k) => k.includes('{') && flagMatches(k, flag));

/** `ScenarioContent.Scenario`. */
const scenarioOf = (b: Bundle, id: string | null | undefined): ScenarioDef | undefined =>
  !id ? undefined : (b.scenarios ?? []).find((x) => x != null && x.id === id);

/** `ContentValidator.FlagWrites`: the flag keys an issue writes (its stages' scenarios and their beat chains, its intro and outro). */
function flagWrites(b: Bundle, m: MissionDef): Set<string> {
  const keys = new Set<string>();
  const chains = new Set<string>();
  for (const st of m.stages ?? []) {
    if (st?.encounter?.beatId) chains.add(st.encounter.beatId);
    const sc = scenarioOf(b, st?.encounter?.scenarioId);
    if (sc == null) continue;
    const actions = [
      ...(sc.triggers ?? []).flatMap((t) => t?.do ?? []),
      ...(sc.onWin ?? []),
      ...(sc.onLose ?? []),
    ];
    for (const a of actions) {
      if (a == null) continue;
      if (a.type === 'set_flag' && a.key) keys.add(a.key);
      if (a.effect?.type === 'flag' && a.effect.flag?.key) keys.add(a.effect.flag.key);
      if (a.type === 'beat' && a.beatId) chains.add(a.beatId);
    }
  }
  const story = m.story!;
  for (const v of [...(story.intro ?? []), ...(story.outro ?? [])])
    if (v?.beatId) chains.add(v.beatId);
  for (const chain of b.beatChains ?? []) {
    if (chain == null || !chains.has(chain.id ?? '')) continue;
    for (const o of Object.values(chain.outcomes ?? {}))
      for (const e of o?.effects ?? []) if (e?.type === 'flag' && e.flag?.key) keys.add(e.flag.key);
  }
  return keys;
}

/** `ContentValidator.FlagReads`: the arc.* flags an issue's own conditions read (cast, intro, outro, cover). */
function flagReads(st: IssueStoryDef): string[] {
  const conds: (CondDef | null | undefined)[] = [
    ...(st.cast ?? []).map((c) => c?.when),
    ...(st.intro ?? []).map((v) => v?.when),
    ...(st.outro ?? []).map((v) => v?.when),
    ...(st.cover?.variants ?? []).map((v) => v?.when),
  ];
  return [...new Set(conds.flatMap((c) => arcFlagsOf(c)))];
}

const isHexColor = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

/** `ContentValidator.Loadout`. `base` is the loadout's path ('' for the issue's own `story.loadout` callers pass it). */
function loadout(
  l: StoryLoadoutDef | null | undefined,
  where: string,
  items: Map<string, ItemDef>,
  at: At,
) {
  if (l == null) return;
  const x = withDefaults(storyLoadoutDefaults(), l);
  const weapon = (k: 'weapon' | 'weapon2') => {
    const id = x[k];
    if (id && !(items.get(id)?.weapon != null)) at(k, `${where} ${k} '${id}' isn't a weapon.`);
  };
  weapon('weapon');
  weapon('weapon2');
  if (x.armor && !(items.get(x.armor)?.armor != null))
    at('armor', `${where} armor '${x.armor}' isn't armor.`);
  (x.items ?? []).forEach((i, n) => {
    if (i == null || !items.has(i.itemId ?? ''))
      at(`items.${n}.itemId`, `${where} carries unknown item '${s(i?.itemId)}'.`);
    else if ((i.quantity ?? 0) < 1 || (i.quantity ?? 0) > MAX_GRANT_QUANTITY)
      at(
        `items.${n}.quantity`,
        `${where} needs item quantity 1..${MAX_GRANT_QUANTITY} ('${s(i.itemId)}').`,
      );
  });
}

type IssueEntry = { m: MissionDef; index: number };

/** `ContentValidator.Location`. */
function location(
  raw: LocationRuleDef | null | undefined,
  w: string,
  arc: ArcDef,
  index: number,
  issueById: Map<string, IssueEntry>,
  categories: Set<string>,
  b: Bundle,
  at: At,
) {
  if (raw == null) {
    at('', `${w} needs a location rule.`);
    return;
  }
  const rule = withDefaults(locationRuleDefaults(), raw);
  const lw = `${w}'s location`;
  if (!has(STORY_LOCATION_KINDS, rule.kind)) {
    at('kind', `${lw} has unknown kind '${s(rule.kind)}' (place, sameAs, picked or anywhere).`);
    return;
  }
  for (const bad of excludedTargets(rule))
    at('', `${lw} could send players to '${bad}' (never homes or places of worship).`);
  if (rule.kind === 'place' || rule.kind === 'picked') {
    if ((rule.kinds?.length ?? 0) === 0 && (rule.categories?.length ?? 0) === 0)
      at('kinds', `${lw} needs kinds or categories to look for.`);
    (rule.kinds ?? []).forEach((k, n) => {
      if (!k || normalizeKind(k) !== k)
        at(`kinds.${n}`, `${lw} has building kind '${s(k)}' (use lower case and _).`);
    });
    (rule.categories ?? []).forEach((c, n) => {
      if (!categories.has(c ?? ''))
        at(`categories.${n}`, `${lw} looks for unknown category '${s(c)}'.`);
    });
    if ((rule.minAreaM2 ?? 0) < 0) at('minAreaM2', `${lw} needs minAreaM2 >= 0.`);
    if ((rule.minRooms ?? 0) < 0 || (rule.minRooms ?? 0) > 16)
      at('minRooms', `${lw} needs minRooms in 0..16.`);
  }
  if (rule.kind === 'picked') {
    if (rule.pickNear !== 'home' && rule.pickNear !== 'player')
      at('pickNear', `${lw} picks near '${s(rule.pickNear)}' (home or player).`);
    if (index === 0)
      at(
        'kind',
        `${lw} is picked, but it's the arc's first issue (the server picks when the previous issue is read).`,
      );
    if (rule.signsFlag && findFlag(b, signsKey(rule.signsFlag, 1)) == null)
      at(
        'signsFlag',
        `${lw} marks the place with flag '${rule.signsFlag}', which isn't in the worldFlags catalogue.`,
      );
  }
  if (rule.kind === 'sameAs') {
    const earlier = (arc.issues ?? []).indexOf(rule.issue ?? '');
    const e = issueById.get(rule.issue ?? '');
    if (e == null || earlier < 0 || earlier >= index)
      at(
        'issue',
        `${lw} goes back to '${s(rule.issue)}', which isn't an earlier issue of arc '${s(arc.id)}'.`,
      );
    else if (e.m.story!.location?.kind === 'anywhere')
      at('issue', `${lw} goes back to '${s(rule.issue)}', which has no place.`);
    if ((rule.fallbackKm ?? 0) < 0 || (rule.fallbackKm ?? 0) > 50)
      at('fallbackKm', `${lw} needs fallbackKm in 0..50 (0 = storyRules.sameAsFallbackKm).`);
  }
}

/** `ContentValidator.StoryRulesSection`. */
function storyRulesSection(b: Bundle, err: Err) {
  if (b.storyRules === null) {
    err('storyRules', undefined, '', "Bundle is missing 'storyRules'.");
    return;
  }
  const r = withDefaults(storyIssueRulesDefaults(), b.storyRules ?? {});
  const w = 'storyRules';
  const range = (k: keyof typeof r, min: number, max: number, field = k) => {
    const v = Number(r[k] ?? 0);
    if (v < min || v > max)
      err('storyRules', undefined, k, `${w} needs ${field} in ${min}..${max}.`);
  };
  range('ticketHours', 0.5, 24);
  range('fallbackSearchKm', 0.1, 20);
  range('storyDropKm', 0.1, 5);
  range('storyDropDays', 1, 30);
  range('sameAsFallbackKm', 0.5, 50);
  range('pickedWithinKm', 0.1, 20);
  range('signsHours', 0, 720);
  range('shortCutsceneAfter', 0, 20);
}

/** `ContentValidator.OnFails`: a scenario lose rule's onFail is "", rewind or branch. */
function onFails(b: Bundle, err: Err) {
  (b.scenarios ?? []).forEach((sc, i) => {
    (sc?.lose ?? []).forEach((e, n) => {
      if (e != null && !(STORY_ON_FAIL as readonly string[]).includes(e.onFail ?? ''))
        err(
          'scenarios',
          i,
          `lose.${n}.onFail`,
          `Scenario '${s(sc!.id)}' lose rule '${s(e.type)}' has onFail '${s(e.onFail)}' ("", rewind or branch).`,
        );
    });
  });
}

/** `ContentValidator.Variants`: intro or outro beat variants. */
function variants(
  list: IssueStoryDef['intro'],
  w: string,
  beats: Set<string>,
  base: string,
  at: At,
) {
  const entries = (list ?? []).map((v, n) => [v, n] as const).filter(([v]) => v != null);
  entries.forEach(([v, n], i) => {
    if (!beats.has(v!.beatId ?? ''))
      at(`${base}.${n}.beatId`, `${w} plays unknown beat chain '${s(v!.beatId)}'.`);
    if (v!.when == null && i < entries.length - 1)
      at(
        `${base}.${n}.when`,
        `${w} has a variant with no condition before the last (only the last may be the default).`,
      );
    storyCondition(v!.when, `${w} variant '${s(v!.beatId)}'`, (m) => at(`${base}.${n}.when`, m));
  });
}

/** Port of `ContentValidator.StorySections`. */
export function validateStoryIssues(b: Bundle, err: Err): void {
  storyIssues(b, err);
  validateMissionRewards(b, err); // ContentValidator.MissionRewards.cs (ADR 0062), right after the story sections like the server
}

function storyIssues(b: Bundle, err: Err): void {
  storyRulesSection(b, err);
  onFails(b, err);

  const arcAt = (i: number) => (path: string, m: string) => err('arcs', i, path, m);
  const rawArcs = b.arcs as (ArcDef | null)[] | null | undefined;
  const arcs = listOf(rawArcs)
    .map((a, i) => [a, i] as const)
    .filter(([a]) => a != null)
    .map(([a, i]) => ({ a: withDefaults(arcDefaults(), a!) as ArcDef, i }));
  if (rawArcs === null) err('arcs', undefined, '', "Bundle is missing 'arcs'.");
  const issues: IssueEntry[] = (b.missions ?? []).flatMap((m, index) =>
    m?.story != null ? [{ m, index }] : [],
  );
  if (arcs.length === 0 && issues.length === 0) return;

  const items = new Map<string, ItemDef>();
  for (const i of b.items ?? []) if (i != null && i.id) items.set(i.id, i);
  const idsOf = (list: ({ id?: string | null } | null)[] | null | undefined) =>
    new Set((list ?? []).filter((x) => x != null).map((x) => x!.id ?? ''));
  const cosmetics = idsOf(b.cosmetics);
  const beats = idsOf(b.beatChains);
  const categories = idsOf(b.categories);
  const triggers = idsOf(b.triggers);
  const issueById = new Map<string, IssueEntry>();
  for (const e of issues) issueById.set(e.m.id ?? '', e);

  // Arcs.
  const arcIds = new Set<string>();
  const orders = new Set<number>();
  const castIds = new Map<string, string>();
  for (const { a, i } of arcs) {
    const at = arcAt(i);
    const w = `Arc '${s(a.id)}'`;
    if (isBlank(a.id)) at('id', 'An arc has no id.');
    else if (arcIds.has(a.id!)) at('id', `Duplicate arc id '${a.id}'.`);
    else arcIds.add(a.id!);
    if (isBlank(a.title)) at('title', `${w} needs a title.`);
    if (!has(STORY_ARC_KINDS, a.kind))
      at('kind', `${w} has unknown kind '${s(a.kind)}' (main, oneoff or tutorial).`);
    const tutorialArc = a.kind === 'tutorial';
    if (!has(MISSION_VISIBILITIES, a.visibility))
      at('visibility', `${w} has unknown visibility '${s(a.visibility)}'.`);
    if (!has(TRIGGER_STATUSES, a.status)) at('status', `${w} has unknown status '${s(a.status)}'.`);
    const order = a.order ?? 0;
    if (orders.has(order))
      at('order', `${w} repeats shelf order ${order} (each arc needs its own).`);
    else orders.add(order);
    const color = a.logo?.color;
    if (color && !isHexColor(color))
      at('logo.color', `${w} has logo colour '${color}' (use #RRGGBB).`);
    const trig = a.unlock?.triggerId;
    if (trig && !triggers.has(trig))
      at('unlock.triggerId', `${w} unlocks from unknown trigger '${trig}'.`);
    const after = a.unlock?.afterIssue;
    if (after) {
      const e = issueById.get(after);
      if (e == null) at('unlock.afterIssue', `${w} unlocks after unknown issue '${after}'.`);
      else if (e.m.story!.arcId === a.id)
        at('unlock.afterIssue', `${w} unlocks after its own issue '${after}'.`);
    }

    const live = a.status === 'live';
    const list = a.issues ?? [];
    if (
      live &&
      !a.continues &&
      !tutorialArc &&
      (list.length < MIN_ISSUES_PER_ARC || list.length > MAX_ISSUES_PER_ARC)
    )
      at(
        'issues',
        `${w} has ${list.length} issues (a finished arc has ${MIN_ISSUES_PER_ARC}–${MAX_ISSUES_PER_ARC}).`,
      );
    else if (list.length > MAX_ISSUES_PER_ARC)
      at('issues', `${w} has ${list.length} issues (at most ${MAX_ISSUES_PER_ARC}).`);
    else if (live && list.length === 0) at('issues', `${w} is live with no issues.`);

    const seenIssues = new Set<string>();
    list.forEach((id, n) => {
      if (seenIssues.has(id ?? '')) at(`issues.${n}`, `${w} lists issue '${s(id)}' twice.`);
      else seenIssues.add(id ?? '');
      const e = issueById.get(id ?? '');
      if (e == null)
        at(`issues.${n}`, `${w} lists '${s(id)}', which isn't a mission with a story block.`);
      else if (e.m.story!.arcId !== a.id)
        at(
          `issues.${n}`,
          `${w} lists issue '${s(id)}', which belongs to arc '${s(e.m.story!.arcId)}'.`,
        );
      else if ((e.m.story!.number ?? 0) !== n + 1)
        at(
          `issues.${n}`,
          `${w} lists issue '${s(id)}' ${n + 1}th, but it's numbered #${e.m.story!.number ?? 0}.`,
        );
    });

    (a.cast ?? []).forEach((raw, n) => {
      if (raw == null) return;
      const c = withDefaults(castDefaults(), raw);
      const cw = `${w} cast '${s(c.id)}'`;
      if (isBlank(c.id)) at(`cast.${n}.id`, `${w} has a cast member with no id.`);
      else if (castIds.has(c.id!))
        at(`cast.${n}.id`, `${cw} repeats a cast id (also in arc '${castIds.get(c.id!)}').`);
      else castIds.set(c.id!, a.id ?? '');
      if (isBlank(c.name)) at(`cast.${n}.name`, `${cw} needs a name.`);
      if ((c.maxHealth ?? 0) < 1 || (c.maxHealth ?? 0) > MAX_CAST_HEALTH)
        at(`cast.${n}.maxHealth`, `${cw} needs maxHealth in 1..${MAX_CAST_HEALTH}.`);
      loadout(c.loadout, cw, items, (p, m) => at(`cast.${n}.loadout.${p}`, m));
    });

    // Endings: first match wins, the last is the default.
    const endings = (a.endings ?? [])
      .map((e, n) => [e, n] as const)
      .filter(([e]) => e != null)
      .map(([e, n]) => [withDefaults(endingDefaults(), e!), n] as const);
    if (live && !a.continues && !tutorialArc && endings.length === 0)
      at(
        'endings',
        `${w} is a finished arc with no ending (add at least one; the last has no condition).`,
      );
    const endingIds = new Set<string>();
    endings.forEach(([e, n], k) => {
      const ew = `${w} ending '${s(e.id)}'`;
      if (isBlank(e.id) || endingIds.has(e.id!))
        at(`endings.${n}.id`, `${w} has a missing or duplicate ending id '${s(e.id)}'.`);
      else endingIds.add(e.id!);
      const lastOne = k === endings.length - 1;
      if (lastOne && e.when != null)
        at(
          `endings.${n}.when`,
          `${w}'s last ending '${s(e.id)}' must have no condition (it's the default).`,
        );
      if (!lastOne && e.when == null)
        at(
          `endings.${n}.when`,
          `${ew} has no condition but isn't last (only the default ending has none).`,
        );
      if (e.cosmeticId && !cosmetics.has(e.cosmeticId))
        at(`endings.${n}.cosmeticId`, `${ew} grants unknown cosmetic '${e.cosmeticId}'.`);
      if (e.beatId && !beats.has(e.beatId))
        at(`endings.${n}.beatId`, `${ew} plays unknown beat chain '${e.beatId}'.`);
      storyCondition(e.when, ew, (m) => at(`endings.${n}.when`, m));
    });

    const teaserNumbers = new Set<number>();
    (a.teasers ?? []).forEach((t, n) => {
      if (t == null) return;
      if (isBlank(t.title)) at(`teasers.${n}.title`, `${w} has a teaser with no title.`);
      const num = t.number ?? 0;
      if (num <= list.length || teaserNumbers.has(num))
        at(
          `teasers.${n}.number`,
          `${w} has teaser #${num} (numbered after the issues, once each).`,
        );
      else teaserNumbers.add(num);
    });
  }

  // Issues.
  const writes = new Map<string, Set<string>>();
  for (const { m } of issues) writes.set(m.id ?? '', flagWrites(b, m));
  const npcs = idsOf(b.npcs);
  const zombies = idsOf(b.zombies);
  const roomTemplates = idsOf(b.roomTemplates);
  for (const { m, index: mi } of issues) {
    const st = withDefaults(issueStoryDefaults(), m.story!) as IssueStoryDef;
    const at: At = (p, msg) => err('missions', mi, p ? `story.${p}` : 'story', msg);
    const w = `Issue '${s(m.id)}'`;
    const arc = arcs.find(({ a }) => a.id === st.arcId)?.a;
    if (arc == null) {
      at('arcId', `${w} belongs to unknown arc '${s(st.arcId)}'.`);
      continue;
    }

    const index = (arc.issues ?? []).indexOf(m.id ?? '');
    if (index < 0) at('arcId', `${w} isn't listed in arc '${s(arc.id)}'.`);
    if (m.triggerId)
      err(
        'missions',
        mi,
        'triggerId',
        `${w} has scan trigger '${m.triggerId}' (an issue sits on the shelf, never on a code).`,
      );
    const live = (m.status ?? 'coming_soon') === 'live' && arc.status === 'live';
    const stageCount = m.stages?.length ?? 0;
    if (stageCount < 1 || stageCount > MAX_ISSUE_STAGES)
      err(
        'missions',
        mi,
        'stages',
        `${w} has ${stageCount} stages (an issue has 1–${MAX_ISSUE_STAGES}).`,
      );
    if ((st.minutes ?? 0) < 0 || (st.minutes ?? 0) > 60)
      at('minutes', `${w} needs minutes in 0..60.`);

    const cast = new Set((arc.cast ?? []).filter((c) => c != null).map((c) => c.id ?? ''));
    if ((st as { heroIsPlayer?: boolean }).heroIsPlayer) {
      if (st.castLead)
        at('castLead', `${w} sets heroIsPlayer and castLead '${s(st.castLead)}' (one or the other).`);
    } else if (!cast.has(st.castLead ?? ''))
      at('castLead', `${w}'s hero '${s(st.castLead)}' isn't in arc '${s(arc.id)}''s cast.`);
    const issueCast = new Set<string>();
    (st.cast ?? []).forEach((raw, n) => {
      if (raw == null) return;
      const c = withDefaults(issueCastDefaults(), raw);
      const cw = `${w} cast '${s(c.castId)}'`;
      if (!cast.has(c.castId ?? ''))
        at(`cast.${n}.castId`, `${cw} isn't in arc '${s(arc.id)}''s cast.`);
      if (c.castId === st.castLead)
        at(`cast.${n}.castId`, `${cw} is the hero (list the others only).`);
      if (issueCast.has(c.castId ?? '')) at(`cast.${n}.castId`, `${cw} is listed twice.`);
      else issueCast.add(c.castId ?? '');
      if (c.loadout != null)
        loadout(c.loadout, cw, items, (p, msg) => at(`cast.${n}.loadout.${p}`, msg));
      storyCondition(c.when, cw, (msg) => at(`cast.${n}.when`, msg));
    });

    loadout(st.loadout, `${w}'s loadout`, items, (p, msg) => at(`loadout.${p}`, msg));
    location(st.location, w, arc, index, issueById, categories, b, (p, msg) =>
      at(p ? `location.${p}` : 'location', msg),
    );

    // Cover.
    const cover = withDefaults(coverDefaults(), st.cover ?? {});
    if (live && isBlank(cover.title)) at('cover.title', `${w}'s cover needs a title.`);
    const panelCtx: PanelCellContext = {
      castRoles: new Set([...cast, 'hero']),
      npcs,
      zombies,
      roomTemplates,
      strings: {},
      rules: b.panelRules ?? {},
      hasChoice: false,
    };
    const coverIds = new Set<string>();
    const cell = (p: PanelSpec, where: string, base: string) =>
      panelCell(p, panelCtx, where, coverIds, new Set<string>(), (path, msg) =>
        at(path ? `${base}.${path}` : base, msg),
      );
    if (cover.panel != null) cell(cover.panel, `${w}'s cover`, 'cover.panel');
    (cover.variants ?? []).forEach((v, n) => {
      if (v == null) return;
      if (v.when == null || v.panel == null)
        at(`cover.variants.${n}`, `${w}'s cover has a variant without a condition or a panel.`);
      if (v.panel != null) cell(v.panel, `${w}'s cover variant`, `cover.variants.${n}.panel`);
      storyCondition(v.when, `${w}'s cover variant`, (msg) => at(`cover.variants.${n}.when`, msg));
    });

    variants(st.intro, `${w}'s intro`, beats, 'intro', at);
    variants(st.outro, `${w}'s outro`, beats, 'outro', at);

    // Dog-ears: on the stage start or a real objective of the stage's scenario.
    const cps = (st.checkpoints ?? []).map((c, n) => [c, n] as const).filter(([c]) => c != null);
    // An arena or overwatch stage plays once (the app seals a one-attempt log), so it needs no dog-ears.
    const firstKind = m.stages?.[0]?.encounter?.kind || 'interior';
    const scene = firstKind === 'arena' || firstKind === 'overwatch';
    const maxCps = arc.kind === 'tutorial' ? MAX_TUTORIAL_CHECKPOINTS : MAX_CHECKPOINTS;
    if (live && !scene && (cps.length < MIN_CHECKPOINTS || cps.length > maxCps))
      at('checkpoints', `${w} has ${cps.length} dog-ears (${MIN_CHECKPOINTS}–${maxCps}).`);
    const cpIds = new Set<string>();
    for (const [c, n] of cps) {
      const cw = `${w} dog-ear '${s(c!.id)}'`;
      if (isBlank(c!.id) || cpIds.has(c!.id!))
        at(`checkpoints.${n}.id`, `${w} has a missing or duplicate dog-ear id '${s(c!.id)}'.`);
      else cpIds.add(c!.id!);
      const stages = m.stages as (MissionStageDef | null)[] | null | undefined;
      const stage = !c!.stage ? stages?.[0] : (stages ?? []).find((x) => x?.id === c!.stage);
      if (stage == null) {
        at(`checkpoints.${n}.stage`, `${cw} is on unknown stage '${s(c!.stage)}'.`);
        continue;
      }
      if (!c!.objective) continue;
      const sc = scenarioOf(b, stage.encounter?.scenarioId);
      if (sc == null || !(sc.objectives ?? []).some((o) => o?.id === c!.objective))
        at(
          `checkpoints.${n}.objective`,
          `${cw} folds on '${c!.objective}', which isn't an objective of stage '${s(stage.id)}''s scenario.`,
        );
    }

    // Rewards: small, and they exist.
    const rewards = withDefaults(storyRewardsDefaults(), st.rewards ?? {}) as ReturnType<
      typeof storyRewardsDefaults
    >;
    const perRun = rewards.perRun ?? [];
    if (perRun.length > MAX_STORY_REWARD_STACKS)
      at(
        'rewards.perRun',
        `${w} rewards ${perRun.length} stacks per run (at most ${MAX_STORY_REWARD_STACKS}).`,
      );
    perRun.forEach((i, n) => {
      if (i == null || !items.has(i.itemId ?? ''))
        at(`rewards.perRun.${n}.itemId`, `${w} rewards unknown item '${s(i?.itemId)}'.`);
      else if ((i.quantity ?? 0) < 1 || (i.quantity ?? 0) > MAX_GRANT_QUANTITY)
        at(
          `rewards.perRun.${n}.quantity`,
          `${w} rewards item quantity 1..${MAX_GRANT_QUANTITY} ('${s(i.itemId)}').`,
        );
    });
    const onLocation = rewards.onLocation ?? [];
    if (onLocation.length > MAX_STORY_REWARD_STACKS)
      at(
        'rewards.onLocation',
        `${w} has ${onLocation.length} on-location bonus stacks (at most ${MAX_STORY_REWARD_STACKS}).`,
      );
    onLocation.forEach((i, n) => {
      if (i == null || !items.has(i.itemId ?? ''))
        at(
          `rewards.onLocation.${n}.itemId`,
          `${w}'s on-location bonus has unknown item '${s(i?.itemId)}'.`,
        );
      else if ((i.quantity ?? 0) < 1 || (i.quantity ?? 0) > MAX_GRANT_QUANTITY)
        at(
          `rewards.onLocation.${n}.quantity`,
          `${w}'s on-location bonus needs item quantity 1..${MAX_GRANT_QUANTITY} ('${s(i.itemId)}').`,
        );
    });
    if (rewards.firstRead && !cosmetics.has(rewards.firstRead))
      at('rewards.firstRead', `${w}'s first read grants unknown cosmetic '${rewards.firstRead}'.`);

    // Flags it reads are written by an earlier issue of its arc.
    const earlier = new Set<string>();
    for (let j = 0; j < index; j++)
      for (const k of writes.get(arc.issues![j] ?? '') ?? []) earlier.add(k);
    for (const flag of flagReads(st))
      if (!writtenBy(earlier, flag))
        at('', `${w} reads flag '${flag}', which no earlier issue writes.`);
  }

  // Endings read flags any issue of their arc writes.
  for (const { a, i } of arcs) {
    const all = new Set<string>();
    for (const id of a.issues ?? []) for (const k of writes.get(id ?? '') ?? []) all.add(k);
    (a.endings ?? []).forEach((e, n) => {
      for (const flag of arcFlagsOf(e?.when))
        if (!writtenBy(all, flag))
          err(
            'arcs',
            i,
            `endings.${n}.when`,
            `Arc '${s(a.id)}' ending '${s(e!.id)}' reads flag '${flag}', which no issue of the arc writes.`,
          );
    });
  }
}
